import { z } from 'zod';

import { GRID_FILTER_OPERATOR_DESCRIPTION, GRID_FILTER_OPERATOR_VALUES } from '../fieldEnums';
import { toCoreClaimNames } from '../v2/claims';
import { toCoreOrderNames } from '../v2/orders';
import { toCoreProductNamesV21 } from '../v2/products';
import { toCoreReceivingNames } from '../v2/receivings';
import { toCoreReturnNames } from '../v2/returns';
import { stringEnum } from './openapiHelpers';

/** Stock-movement grid columns (same whitelist as GWMS Joi `StockMovementColumns`). */
export const STOCK_MOVEMENT_COLUMN_VALUES = [
    'count',
    'createdAt',
    'updatedAt',
    'expirationDate',
    'id',
    'lot',
    'sku',
    'type',
] as const;

type NonEmptyStringTuple = readonly [string, ...string[]];

const keysAsTuple = (map: Record<string, string>): NonEmptyStringTuple => {
    const keys = Object.keys(map);
    if (keys.length === 0) {
        throw new Error('Grid column map must not be empty');
    }
    return keys as unknown as NonEmptyStringTuple;
};

const operatorSchema = stringEnum(
    GRID_FILTER_OPERATOR_VALUES,
    GRID_FILTER_OPERATOR_DESCRIPTION,
);

const gridSortDirectionSchema = z.enum(['ASC', 'DESC', 'asc', 'desc']).describe('Sort direction.');

/**
 * Echo of the filter body in grid responses — column names stay plain strings
 * (resource-specific enums belong on the request body of each filter operation).
 */
export const v21GridRequestEchoSchema = z.object({
    page: z.number().int().min(1).optional().describe('Page number (default 1).'),
    items: z.number().int().min(1).max(1000).optional().describe('Page size (default 10, max 1000).'),
    columns: z.array(z.string()).optional().describe(
        'Fields returned per row; empty array or omit = all columns for this grid.',
    ),
    sorts: z.array(z.object({
        column: z.string().describe('Column that was sorted.'),
        direction: gridSortDirectionSchema,
    }).strict()).optional().describe('Sort order applied to the result.'),
    filters: z.array(z.array(z.object({
        column: z.string().describe('Column that was filtered.'),
        operator: operatorSchema,
        value: z.unknown().describe(
            'Scalar, array, or null — depends on operator (e.g. in/nin/between expect arrays).',
        ),
    }).strict())).optional().describe(
        'OR of AND-groups that were applied.',
    ),
}).strict();

/**
 * Per-resource v2.1 filter body. Column enums apply to `columns`, `sorts[].column`, and `filters[][].column`.
 * No shared mega-description — each endpoint documents only its own columns.
 */
export const buildV21GridRequestSchema = (columns: NonEmptyStringTuple) => {
    const columnSchema = stringEnum(
        columns,
        'Column name for this grid (use in `columns`, `sorts`, and `filters`).',
    );

    const filterConditionSchema = z.object({
        column: columnSchema,
        operator: operatorSchema,
        value: z.unknown().describe(
            'Scalar, array, or null — depends on operator (e.g. in/nin/between expect arrays).',
        ),
    }).strict();

    const sortSchema = z.object({
        column: columnSchema,
        direction: gridSortDirectionSchema,
    }).strict();

    return z.object({
        page: z.number().int().min(1).optional().describe('Page number (default 1).'),
        items: z.number().int().min(1).max(1000).optional().describe('Page size (default 10, max 1000).'),
        columns: z.array(columnSchema).optional().describe(
            'Fields to return per row; empty array or omit = all columns for this grid.',
        ),
        sorts: z.array(sortSchema).optional().describe(
            'Sort order. Append {"column":"id","direction":"ASC"} as tiebreaker when exporting all pages.',
        ),
        filters: z.array(z.array(filterConditionSchema)).optional().describe(
            'OR of AND-groups: outer array = OR, each inner array = AND of conditions.',
        ),
    }).strict();
};

export const productsV21GridRequestSchema = buildV21GridRequestSchema(keysAsTuple(toCoreProductNamesV21));
export const ordersV21GridRequestSchema = buildV21GridRequestSchema(keysAsTuple(toCoreOrderNames));
export const receivingsV21GridRequestSchema = buildV21GridRequestSchema(keysAsTuple(toCoreReceivingNames));
export const returnsV21GridRequestSchema = buildV21GridRequestSchema(keysAsTuple(toCoreReturnNames));
export const claimsV21GridRequestSchema = buildV21GridRequestSchema(keysAsTuple(toCoreClaimNames));
export const stockMovementsV21GridRequestSchema = buildV21GridRequestSchema(STOCK_MOVEMENT_COLUMN_VALUES);

/** @deprecated Prefer a resource-specific schema (`productsV21GridRequestSchema`, …). */
export const v21GridRequestSchema = productsV21GridRequestSchema;

export type V21GridRequest = z.infer<typeof productsV21GridRequestSchema>;
